import { useState } from 'react';

import { CaptionRegular } from '@components/atoms/typography';

type TeamBadgeProps = {
  shortName: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
};

const sizeClass: Record<NonNullable<TeamBadgeProps['size']>, string> = {
  xs: 'w-4 h-4',
  sm: 'w-5 h-5',
  md: 'w-7 h-7',
  lg: 'w-9 h-9'
};

const initialFallbackClass: Record<
  NonNullable<TeamBadgeProps['size']>,
  string
> = {
  xs: 'text-[8px]',
  sm: 'text-[8px]',
  md: 'text-[10px]',
  lg: 'text-xs'
};

/**
 * Renders a club crest from the public R2 bucket via the /badges/:path*
 * Next.js rewrite, falling back to the team short name in a tinted
 * circle when the image fails to load (e.g. unknown opponent codes).
 */
export const TeamBadge = ({
  shortName,
  size = 'md',
  className
}: TeamBadgeProps) => {
  const [hasError, setHasError] = useState(false);
  const dimensions = sizeClass[size];

  if (hasError || !shortName) {
    return (
      <span
        className={`${dimensions} inline-flex items-center justify-center rounded-full bg-neutral-200 text-neutral-700 font-semibold ${
          initialFallbackClass[size]
        } ${className ?? ''}`}
        data-cy="team-badge-fallback"
        data-cy-team-code={shortName}
      >
        <CaptionRegular className="mb-0" textColor="text-neutral-700">
          {shortName?.slice(0, 3) ?? '—'}
        </CaptionRegular>
      </span>
    );
  }

  return (
    <img
      src={`/badges/${shortName}.png`}
      alt={shortName}
      className={`${dimensions} object-contain ${className ?? ''}`}
      onError={() => setHasError(true)}
      data-cy="team-badge"
      data-cy-team-code={shortName}
    />
  );
};
